import React from 'react';
import PropTypes from 'prop-types';
import { Formik, Field } from 'formik';
import { motion } from 'framer-motion';

import Button from 'common/components/Button';
import Heading from 'common/components/Heading';
import Text from 'common/components/Text';
import TextBox from 'common/components/TextBox';
import TextArea from 'common/components/TextArea';
import formUtils from 'common/formUtils.js';

import styles from './index.module.css';

const variants = {
  visible: { opacity: 1 },
  hidden: { opacity: 0 },
};

const initialValues = {
  amount: '',
  description: '',
};

const amountValidation = (value) =>
  formUtils.validatePositiveAmount(value) || formUtils.validateNumber(value);

const descriptionValidation = (value) => formUtils.validateRequired(value);

const NewOffRamp = ({
  onSubmit,
  settlementCurrency,
  submitting,
  errorMessage,
}) => (
  <Formik initialValues={initialValues} onSubmit={onSubmit}>
    {({ handleSubmit, errors, touched }) => (
      <motion.div
        initial="hidden"
        animate="visible"
        variants={variants}
        className={styles.root}
      >
        <form onSubmit={handleSubmit}>
          <Heading level="2" size="3" weight="medium">
            Choose how much to off ramp
          </Heading>

          <div className={styles.description}>
            <Text size="caption" color="rebranding-md-color-grey-60">
              Amount is calculated in your current settlement currency (
              {settlementCurrency}), added to your next payout, and will be
              transferred directly to your bank account.
            </Text>
          </div>

          <div className={styles.section}>
            <Heading level="3" size="5" weight="regular">
              Amount
            </Heading>
            <div className={styles.input}>
              <Field name="amount" validate={amountValidation}>
                {({ field, meta }) => (
                  <TextBox
                    id="amount"
                    labelType="amount"
                    placeholder={`${settlementCurrency} amount to receive`}
                    error={meta.touched && meta.error}
                    errorText={meta.error}
                    {...field}
                  />
                )}
              </Field>
            </div>
          </div>

          <div className={styles.section}>
            <Heading level="3" size="5" weight="regular">
              Description
            </Heading>
            <div className={styles.input}>
              <Field name="description" validate={descriptionValidation}>
                {({ field, meta }) => (
                  <TextArea
                    id="description"
                    labelText=""
                    labelType="description"
                    placeholder=""
                    rows={4}
                    hideLabel
                    error={meta.touched && meta.error}
                    errorText={meta.error}
                    {...field}
                  />
                )}
              </Field>
              <div className={styles.helperText}>
                <Text color="rebranding-md-color-grey-60" size="caption">
                  Banks may require detailed information about the transfer of
                  these funds. Providing it now helps us act faster if needed.
                </Text>
              </div>
            </div>
          </div>

          <div className={styles.formFooter}>
            <div className={styles.buttonWrapper}>
              <Button
                kind="primary"
                loading={submitting}
                full
                disabled={formUtils.isSubmitDisabled(errors, touched)}
              >
                <button
                  type="submit"
                  id="create-off-ramp-button"
                  data-test="create-off-ramp-button"
                >
                  Create off ramp
                </button>
              </Button>
            </div>
            {errorMessage && (
              <div className={styles.errorMessage}>
                <Text color="rebranding-md-color-red">
                  Something went wrong. Please try again.
                </Text>
              </div>
            )}
          </div>
        </form>
      </motion.div>
    )}
  </Formik>
);
NewOffRamp.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  settlementCurrency: PropTypes.string.isRequired,
  submitting: PropTypes.bool.isRequired,
  errorMessage: PropTypes.string,
};

NewOffRamp.defaultProps = {
  errorMessage: null,
};

export default NewOffRamp;
